import React, { useCallback, useEffect, useState } from 'react';

import { Alert, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import useRecordMerchantSync from '@hooks/operator/useRecordMerchantSync';
import PageHeader from '@shared/PageHeader';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';

import { replenishmentService } from '@services/operator/replenishmentService';
import type { ReplenishmentSlot } from '@services/operator/replenishmentService';

import SlotForm from './components/SlotForm';

/**
 * ReplenishmentShelvesEdit — 編輯揀補儲位 slot 設定（獨立頁，取代原 SlotFormDrawer）。
 *
 * 載入模式對齊 ProductionBoms/Edit + PurchaseOrders/Edit + ReplenishmentOrders/Show：
 *   useParams id → getShelf(id) → handleRecordLoaded(slot) 把 currentMerchant 對齊到
 *   slot 所屬 merchant，期間 merchantDataLoading 為 true 擋住表單，避免 product /
 *   inventory_type 下拉短暫顯示前一個 merchant 的清單。SlotForm 的 merchantId 跟著
 *   currentMerchant?.id 走（而非 header 當前 merchant）。
 */
const ReplenishmentShelvesEdit: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { currentMerchant } = useOperatorData();
  const { handleRecordLoaded, merchantDataLoading } = useRecordMerchantSync();

  const [loading, setLoading] = useState(true);
  const [slot, setSlot] = useState<ReplenishmentSlot | null>(null);

  const loadSlot = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await replenishmentService.getShelf(id);
      if (res.success) {
        setSlot(res.data);
        // 把 currentMerchant 對齊到 slot 所屬 merchant（merchant-scope 鎖定）。
        handleRecordLoaded(res.data);
      } else {
        handleApiError(res, { t, fallbackMessage: t('common.error.load_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t, fallbackMessage: t('common.error.load_failed') });
    } finally {
      setLoading(false);
    }
  }, [id, t, handleRecordLoaded]);

  useEffect(() => {
    loadSlot();
  }, [loadSlot]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!slot) {
    return (
      <Alert
        message={t('operator.replenishment_shelves.form.edit_title')}
        description={t('common.error.not_found')}
        type="warning"
        showIcon
      />
    );
  }

  return (
    <div>
      <PageHeader
        items={[
          'replenishment',
          'replenishment.shelves',
          { label: t('operator.replenishment_shelves.form.edit_title') },
        ]}
      />
      <SlotForm
        mode="edit"
        merchantId={currentMerchant?.id ?? null}
        initialSlot={slot}
        disabled={merchantDataLoading}
        onSaved={() => navigate('/replenishment_shelves')}
        onCancel={() => navigate('/replenishment_shelves')}
      />
    </div>
  );
};

export default ReplenishmentShelvesEdit;
